/* Video carousel (OL-3537): empty-state guard.

   Tolstoy's app block decides client-side, after its own script loads,
   whether it has any videos tagged for the current product - Liquid has
   no way to know that at render time.

   FAILS OPEN: the section is visible from first paint (no `hidden`
   attribute anywhere), matching exactly what it gets in the theme
   editor, where this demonstrably works. This file only ever HIDES the
   wrapper afterwards, and only once it has confirmed the embed slot
   rendered nothing within a grace period.

   A prior version started the wrapper `hidden` and revealed it once
   content was confirmed. That broke the section for every real
   customer, including ones with videos correctly tagged: `hidden` is
   equivalent to display:none, which removes the whole subtree from the
   render tree, and third-party embeds very commonly check their own
   visibility/dimensions before deciding whether to initialise at all -
   a defensive pattern against exactly that scenario. Tolstoy never got
   a fair chance to detect it should render. It only ever worked in the
   theme editor because that path explicitly skipped the hidden-by-
   default state.

   Trade-off of failing open: a product with genuinely zero tagged
   videos may show a brief flash of an empty section before this hides
   it. Accepted deliberately - a rare, brief cosmetic flash beats the
   section not working for anyone.

   The grace-period timeout below decides whether to hide - it is not a
   final verdict. The observer keeps running past it, so a widget that
   is merely slow (bad connection, slow third-party script) still
   reveals the section the moment it actually renders, rather than
   staying hidden for the rest of the page view just because it missed
   an arbitrary cutoff. A prior version disconnected the observer at the
   timeout, which reintroduced - on an 8s delay - the exact "doesn't work
   for a real customer with real content" failure this file's fail-open
   rewrite exists to prevent (OL-3537 review). */
(function () {
  var GRACE_PERIOD_MS = 8000;
  var MIN_HEIGHT_PX = 24;

  function hasRenderedContent(embed) {
    if (!embed || embed.children.length === 0) return false;
    return embed.getBoundingClientRect().height >= MIN_HEIGHT_PX;
  }

  function guard(wrapper) {
    if (wrapper.dataset.videoCarouselGuarded) return;
    wrapper.dataset.videoCarouselGuarded = 'true';

    // Theme editor: never auto-hide, so the dashed empty-state
    // placeholder (section-video-carousel.css) stays visible for
    // merchants configuring the block, regardless of whether Tolstoy
    // has videos for whatever product is currently being previewed.
    if (window.Shopify && window.Shopify.designMode) return;

    var embed = wrapper.querySelector('.video-carousel__embed');
    if (!embed) return;

    // true once real content has been confirmed - nothing left to watch
    // for after that, so the observer disconnects for good.
    var resolved = false;
    var observer;
    var timeoutId;

    var reveal = function () {
      if (resolved) return;
      resolved = true;
      clearTimeout(timeoutId);
      if (observer) observer.disconnect();
      wrapper.hidden = false;
    };

    if (hasRenderedContent(embed)) {
      reveal();
      return;
    }

    observer = new MutationObserver(function () {
      // A short delay lets Tolstoy finish laying out this batch of
      // tiles before measuring, rather than judging mid-render.
      requestAnimationFrame(function () {
        if (hasRenderedContent(embed)) reveal();
      });
    });
    observer.observe(embed, { childList: true, subtree: true });

    // Give Tolstoy a reasonable window before deciding whether to hide -
    // but this only sets the VISIBLE state, it doesn't stop watching.
    // The observer above is still connected, so content that finishes
    // rendering after this fires still calls reveal() and un-hides the
    // section; hiding here is not the last word the way disconnecting
    // would make it.
    timeoutId = setTimeout(function () {
      if (!resolved && !hasRenderedContent(embed)) wrapper.hidden = true;
    }, GRACE_PERIOD_MS);
  }

  function init(root) {
    (root || document).querySelectorAll('[data-video-carousel-guard]').forEach(guard);
  }

  document.addEventListener('DOMContentLoaded', function () {
    init();
  });

  // Theme editor: sections are re-rendered on edit, so re-bind the new nodes.
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
})();
